import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MonoFieldValueAccessorComponent } from '../mono-field-value-accessor.component';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALUE_ACCESSOR,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SdFormInputComponent } from '../form-input/form-input.component';
import { isPlatformBrowser, NgClass, NgTemplateOutlet } from '@angular/common';
import { Subscription } from 'rxjs';
import { ISdFormComponentItem } from '../form-component-interface.model';
import { SdFormRadioGroupComponent } from '../form-radio-group/form-radio-group.component';
import { SdFormCheckboxGroupComponent } from '../form-checkbox-group/form-checkbox-group.component';
import { ErrorTranslateService } from '../error-translate-service';
import { BaseFormErrorTranslateService } from '../form-common-error/base-form-error-translate.service';
import { BrandsEnum } from '../../../_enums/brands.enum';
import { DeviceService } from '../../../_services/device.service';
import { IconSizeEnum } from '../../../_enums/icon.enum';
import { DropdownType } from '../../dropdowns/dropdown/dropdown.enum';
import { SdDropdownComponent } from '../../dropdowns/dropdown/dropdown.component';
import { SdListComponent } from '../../lists/list/list.component';
import { SdHtmlComponent } from '../../html/html.component';

@Component({
  selector: 'sd-form[autocomplete]',
  templateUrl: './form-autocomplete.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SdFormAutocompleteComponent)
    },
    { provide: BaseFormErrorTranslateService, useClass: ErrorTranslateService }
  ],
  standalone: true,
  imports: [
    SdDropdownComponent,
    SdFormInputComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    SdListComponent,
    SdFormCheckboxGroupComponent,
    NgTemplateOutlet,
    SdFormRadioGroupComponent,
    SdHtmlComponent
  ]
})
export class SdFormAutocompleteComponent
  extends MonoFieldValueAccessorComponent<any>
  implements ControlValueAccessor, AfterViewInit, OnDestroy, OnInit
{
  constructor(
    cdRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: any,
    protected elRef: ElementRef,
    private deviceService: DeviceService,
  ) {
    super(cdRef);
    this.inputSubscription = this.inputControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(() => {
        if (this.inputValue?.length >= this.minLength) {
          this.notifyChangedInput();
          if (this._elements.length > 0 && !this.isSelect) {
            this.isOpened = true;
          }
        } else {
          if (!this.isSelect) {
            this.isOpened = false;
          }
        }
      });
  }

  inputSubscription: Subscription;
  inputControl: UntypedFormControl = new UntypedFormControl();
  inputValue: string;
  elWidth = 0;
  dropdownType = DropdownType.select;
  iconSize = IconSizeEnum.small;
  _elements: any[] = [];
  initialElements: any[] = [];
  deviceSubscription: Subscription;
  isDevice$: boolean;

  @Input()
  brand: BrandsEnum;

  @Input()
  idField = '';

  @Input()
  disabled = false;

  @Input()
  clearAfterSelectElement = true;

  /**
   * Minimum string length before performing a search
   */
  @Input()
  minLength = 3;

  /**
   * Maximum number of results displayed (does not include the option added by "withAllResultsContaining")
   */
  @Input()
  maxDisplayedResults: number;

  /**
   * List of proposed elements, corresponding to the search
   */
  @Input()
  set elements(elements: any[]) {
    this._elements = this.maxDisplayedResults
      ? elements?.slice(0, this.maxDisplayedResults)
      : elements;

    // If no result, no display
    if (this.inputValue) {
      this.isOpened = this._elements?.length > 0;
    }

    this.initialElements = this._elements;
  }

  /**
   * List of the properties of the elements to be displayed, in the form of a string array
   * The desired display order must be respected
   * Ex: ['name', 'nni'] ==> then the input elements must be of the form :
   * [{ name: 'Jacques', nni: 'JQ234CIA'}, { name: 'Paul', nni: 'PH561FQF'}, ...]
   */
  @Input()
  properties: string[];

  /**
   * Event emitted when typing a search
   */
  @Output()
  inputChanged = new EventEmitter<string>();

  @Output()
  isOpenedChange = new EventEmitter<boolean>();

  @Output()
  formChange = new EventEmitter<ISdFormComponentItem>();

  @Input()
  isOpened = false;

  @Input()
  optionsAreCheckboxes = false;

  @Input()
  isSelect = false;

  @Input()
  valueKey: string;

  @Input()
  libelleKey: string;

  @Input()
  libelle: string;

  @Input()
  groupLibelleTemplate: TemplateRef<any>;

  @Input()
  displayWidth: number = null;

  @ViewChild('input', { read: ElementRef })
  input: ElementRef;

  @ViewChild('input')
  inputComponent: SdFormInputComponent;

  @Input() class = '';
  @HostBinding('class')
  get hostClasses(): string {
    return [this.class, this.getClasses()].filter(Boolean).join(' ');
  }

  ngOnInit() {
    super.ngOnInit();
    if (isPlatformBrowser(this.platformId) && this.displayWidth) {
      this.elRef.nativeElement.style.setProperty(
        'width',
        this.displayWidth / 10 + 'rem',
      );
    }
    this.deviceSubscription = this.deviceService.isDevice.subscribe((x) => {
      this.isDevice$ = x;
      this.cdRef.markForCheck();
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.setElementWidth();
    }
  }

  selectElement(element) {
    this.propagateChange(element);
    this.inputValue = this.clearAfterSelectElement
      ? ''
      : element[Object.keys(element)[0]];
    this.inputComponent.isFilled = this.clearAfterSelectElement
      ? ''
      : element[Object.keys(element)[0]];
    this.isOpened = false;
    this.cdRef.markForCheck();
  }

  updateElements() {
    const elLibelle = this.optionsAreCheckboxes ? 'libelle' : this.libelleKey;
    this._elements = this.initialElements;
    this._elements = this._elements?.filter((element) => {
      if (
        element[elLibelle].toLowerCase().includes(this.inputValue.toLowerCase())
      ) {
        return element;
      } else {
        return null;
      }
    });
  }

  mappingChildToParent(value: any): any {
    return value;
  }

  mappingParentToChild(value: any): any {
    return value;
  }

  private notifyChangedInput() {
    this.propagateChange(this.inputValue);
    this.inputChanged.emit(this.inputValue);
    if (this.isSelect) {
      this.updateElements();
    }
    this.cdRef.markForCheck();
  }

  setElementWidth() {
    this.elWidth = this.input.nativeElement.getBoundingClientRect().width;
  }

  onClear() {
    this.inputValue = '';
    this.inputComponent.isFilled = false;
    this.notifyChangedInput();
  }

  onChangeAutocompleteItems(event) {
    this.formChange.emit(event);
  }

  itemClickSelected(item) {
    this.formChange.emit(item);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.inputSubscription?.unsubscribe();
  }
}
